import * as React from 'react';
import { TYPOGRAPHY_SIZE } from 'mm-theme-configuration/dist/consts';
import { css, StyleSheet, CSSProperties } from 'aphrodite/no-important';
import { useTheme } from '../../theming/useTheme';
import { ErrorBoundary } from '../errorHandling/ErrorBoundary';
import { QuoteIcon } from './QuoteIcon';
import { MandatoryAdditionalStyle } from '../components.utils';
import { H3 } from '../../typography/semanticTags';
import { semanticCSS } from '../../typography/semanticTags/semanticTags.utils';
import { BaseUnit } from '../../theming/baseUnitDefinition';
import { TranslatedFontSize } from '../../theming/fontSizeTranslator';
import { MEDIA_BREAKPOINTS } from '../../mediaQueries.const';

const componentConfigurationKey = 'quote';

interface QuoteComponentDataProps {
  text: string;
  cite: string;
  dataId?: string;
}

interface QuoteComponentThemeProps {
  baseUnit: BaseUnit;
  borderColor: string;
  textColor: string;
  citeColor: string;
  iconColor: string;
  fontSizesStyles: TranslatedFontSize;
}

type QuoteComponentProps = QuoteComponentDataProps & MandatoryAdditionalStyle;

const createCiteStyle = (fontSizesStyles: TranslatedFontSize): { [key: string]: CSSProperties } => {
  const citeFontSize = fontSizesStyles[TYPOGRAPHY_SIZE.NORMAL];
  const { style: { fontFamily, fontWeight }, breakpoints: { large, medium, small } } = citeFontSize;
  const fontFamilyConverted = fontFamily as unknown as CSSProperties;
  const fontWeightConverted = fontWeight as unknown as CSSProperties;
  return {
    fontFamily: fontFamilyConverted,
    fontWeight: fontWeightConverted,
    [MEDIA_BREAKPOINTS.large]: {
      ...large,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      ...medium,
    },
    [MEDIA_BREAKPOINTS.small]: {
      ...small,
    },
  };
};

const getStyles = (theme: QuoteComponentThemeProps) => {
  const { baseUnit, borderColor, textColor, citeColor, fontSizesStyles } = theme;
  const citeStyles = createCiteStyle(fontSizesStyles);

  return StyleSheet.create({
    wrapper: {
      position: 'relative',
      borderBottom: `1px solid ${borderColor}`,
      margin: `${baseUnit}px 0 0 0`,
      paddingBottom: `${baseUnit * 0.5}px`,
    },
    text: {
      fontStyle: 'italic',
      color: textColor,
      marginTop: `${baseUnit * 0.5}px`,
    },
    cite: {
      color: citeColor,
      fontStyle: 'normal',
      marginTop: `${baseUnit * 0.25}px`,
      ...citeStyles,
    },
    quoteTop: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      alignItems: 'center',
      ':before': {
        content: '""',
        display: 'block',
        height: '1px',
        borderTop: `1px solid ${borderColor}`,
        flex: '2 0 0',
      },
      ':after': {
        content: '""',
        display: 'block',
        height: '1px',
        borderTop: `1px solid ${borderColor}`,
        flex: '2 0 0',
      },
    },
  });
};

export const Quote: React.FunctionComponent<QuoteComponentProps> = props => {
  const { text, cite, additionalStyle, dataId } = props;
  const themeProps = useTheme<QuoteComponentThemeProps>(componentConfigurationKey);
  const { iconColor, baseUnit } = themeProps;
  const styles = getStyles(themeProps);

  return (
    <ErrorBoundary>
      <blockquote data-id={dataId} className={css(styles.wrapper, additionalStyle.style)}>
        <div className={css(styles.quoteTop)}>
          <QuoteIcon iconColor={iconColor} baseUnit={baseUnit} />
        </div>
        <H3 styles={semanticCSS(styles.text)}>{text}</H3>
        {
          cite ? (
            <cite className={css(styles.cite)}>
              -&nbsp;
              {cite}
            </cite>
          ) : null
        }
      </blockquote>
    </ErrorBoundary>
  );
};
